import * as React from 'react';
import dayjs from 'dayjs';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid2';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import useAxios from '../utils/useAxios';
import { ShellContext } from './layout/AppShell';
import { NumberField, SectionCard, currency } from './ui';
import { TRUE_COLORS } from '../theme';

type Entry = { id: number; kind: string; label: string; amount: number; method: string; note: string; created_at: string; folio: string | null };
type Status = { balance: number; paid: number; bonus: number; expiring: { amount: number; expires_at: string }[]; entries: Entry[]; program: { active: boolean } };

const METHODS: Record<string, string> = { cash: 'efectivo', card: 'tarjeta', online: 'en línea' };

/** A customer's prepaid balance in their page (Panel › Clientes): what they paid, their bonus, its movements, and an
 *  administrator's adjustment. Hidden for businesses that don't use the balance. */
const ClientBalance = ({ clientId }: { clientId: string | number }) => {
  const axios = useAxios();
  const { membership } = React.useContext(ShellContext);
  const [status, setStatus] = React.useState<Status | null>(null);
  const [adjusting, setAdjusting] = React.useState(false);
  const [sign, setSign] = React.useState<'add' | 'remove'>('remove');
  const [amount, setAmount] = React.useState<number | null>(null);
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    axios.get(`api/balance/customers/${clientId}/`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [axios, clientId]);

  if (!status || (!status.program.active && status.entries.length === 0)) return null;

  const adjust = async () => {
    if (!amount) { setError('Escribe el monto.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post('api/balance/adjustments/', { client: clientId, amount: sign === 'remove' ? -amount : amount, note });
      setStatus(current => current && { ...current, ...data });
      setAdjusting(false);
      setAmount(null);
      setNote('');
    } catch (err: any) {
      setError(err?.response?.data?.response ?? 'No se pudo ajustar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid size={{ xs: 12, md: 4 }}>
    <SectionCard title="Saldo" action={membership?.is_admin ? <Button size="small" onClick={() => setAdjusting(true)}>Ajustar</Button> : undefined}>
      <Typography sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.1 }} data-testid="client-balance">{currency(status.balance)}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {currency(status.paid)} pagado{status.bonus > 0 ? ` · ${currency(status.bonus)} de regalo` : ''}
      </Typography>
      {status.expiring.map((lot, i) => (
        <Typography key={i} variant="body2" sx={{ color: TRUE_COLORS.warning }}>
          {currency(lot.amount)} de regalo vence el {dayjs(lot.expires_at).format('D MMM YYYY')}
        </Typography>
      ))}
      {status.entries.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {status.entries.map(entry => (
            <Stack key={entry.id} direction="row" sx={{ py: 0.75, borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.label}{entry.method ? ` · ${METHODS[entry.method] ?? entry.method}` : ''}{entry.folio ? ` · venta ${entry.folio}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {dayjs(entry.created_at).format('D MMM YYYY, HH:mm')}{entry.note ? ` · ${entry.note}` : ''}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, color: entry.amount < 0 ? 'text.primary' : TRUE_COLORS.success }}>
                {entry.amount > 0 ? '+' : '−'}{currency(Math.abs(entry.amount))}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}

      <Dialog open={adjusting} onClose={() => !busy && setAdjusting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajustar saldo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <ToggleButtonGroup exclusive fullWidth value={sign} onChange={(_, v) => v && setSign(v)}>
              <ToggleButton value="remove">Descontar</ToggleButton>
              <ToggleButton value="add">Agregar</ToggleButton>
            </ToggleButtonGroup>
            <NumberField label="Monto ($)" value={amount} onChange={setAmount} autoFocus
              helperText={sign === 'remove' ? `Hasta ${currency(status.paid)} (lo que pagó). P. ej. si le devuelves su dinero.` : 'P. ej. para corregir una recarga.'} />
            <TextField label="Motivo" value={note} onChange={e => setNote(e.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }}
              placeholder={sign === 'remove' ? 'Devuelto en efectivo' : 'Corrección'} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdjusting(false)} disabled={busy}>Cancelar</Button>
          <Button variant="contained" onClick={adjust} disabled={busy || !amount || !note.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
    </Grid>
  );
};

export default ClientBalance;
